/**
 * Where `useDraft` keeps unsaved text on the device, and how it knows the server has it (S 2.7). Free of React so the rules are tested on
 * their own (scripts/dev/test/save-feedback.test.mjs).
 */

export const DRAFT_PREFIX = "ncp.draft.";
/** Raised by the API client after the API stored a request (`STORED_EVENT` in @ncp/api-client's http.ts; the test keeps the two equal). */
export const STORED_EVENT = "ncp:stored";
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

export interface StoredDraft { value: string; savedAt: number }

export type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function readDraft(storage: DraftStorage, key: string, now = Date.now()): StoredDraft | null {
  try {
    const raw = storage.getItem(DRAFT_PREFIX + key);
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredDraft;
    if (typeof s.value !== "string" || typeof s.savedAt !== "number" || now - s.savedAt > DRAFT_MAX_AGE_MS) {
      storage.removeItem(DRAFT_PREFIX + key);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function writeDraft(storage: DraftStorage, key: string, value: string, now = Date.now()): void {
  try { storage.setItem(DRAFT_PREFIX + key, JSON.stringify({ value, savedAt: now } satisfies StoredDraft)); } catch { /* storage unavailable or full */ }
}

export function removeDraft(storage: DraftStorage, key: string): void {
  try { storage.removeItem(DRAFT_PREFIX + key); } catch { /* storage unavailable */ }
}

/** Text compared without the whitespace an editor or a form adds or drops (line endings, trailing spaces, blank lines). */
export function normaliseText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Every text a request carried: the string values of its JSON body at any depth (and of a JSON document sent as a string, such as a
 * report's edited passages), the lines of a list joined as the field held them, and the text entries of a form upload.
 */
export function textsOf(body: unknown, out: Set<string> = new Set(), depth = 0): Set<string> {
  if (depth > 8 || body === null || body === undefined) return out;
  if (typeof body === "string") {
    const t = normaliseText(body);
    if (t) out.add(t);
    const head = body.trimStart()[0];
    if ((head === "{" || head === "[") && body.length < 2_000_000) {
      try { textsOf(JSON.parse(body), out, depth + 1); } catch { /* not JSON */ }
    }
    return out;
  }
  if (Array.isArray(body)) {
    for (const item of body) textsOf(item, out, depth + 1);
    if (body.length > 1 && body.every((item) => typeof item === "string")) {
      const lines = normaliseText((body as string[]).join("\n"));
      if (lines) out.add(lines);
    }
    return out;
  }
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    body.forEach((v) => { if (typeof v === "string") textsOf(v, out, depth + 1); });
    return out;
  }
  if (typeof body === "object") for (const v of Object.values(body as Record<string, unknown>)) textsOf(v, out, depth + 1);
  return out;
}

/** Whether a stored request carried this text, so the copy kept on the device is no longer needed. */
export function carries(body: unknown, text: string): boolean {
  const t = normaliseText(text);
  return !!t && textsOf(body).has(t);
}
